import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Command, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Command Bar - Persistent Top Bar (Control Panel, not Toolbar)
 * 
 * Desktop-first design. Shows only essential operational signals:
 * - App identity (minimal)
 * - Current machine + status
 * - Live time & session uptime
 * - Command palette trigger
 * 
 * Removed redundancy: Focus mode, runtime selector, capabilities dropdown
 * (these are available elsewhere or via command palette)
 */
const CommandBar = ({
  machineName,
  machineHash,
  machineStatus = 'unknown',
  syncStatus = 'synced',
  onCommandPaletteOpen,
  onRefresh
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sessionStart] = useState(() => new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Format uptime as HH:MM:SS
  const formatUptime = () => {
    const diff = Math.floor((currentTime - sessionStart) / 1000);
    const hours = Math.floor(diff / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
    const seconds = (diff % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  // Format current time
  const formatTime = () => {
    return currentTime.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const statusColors = {
    valid: 'bg-green-500/20 text-green-400 border-green-500/30',
    invalid: 'bg-red-500/20 text-red-400 border-red-500/30',
    dirty: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    syncing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    unknown: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  const syncColors = {
    synced: 'text-green-400',
    offline: 'text-yellow-400',
    conflict: 'text-red-400'
  };

  return (
    <header 
      className="h-10 border-b border-white/10 bg-black/60 backdrop-blur-xl flex items-center justify-between px-4 flex-shrink-0"
      data-testid="command-bar"
    >
      {/* Left: App Identity + Machine Info */}
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        {/* App Identity */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          <img 
            src="/firmastate-icon.png" 
            alt="FIRMASTATE" 
            className="w-6 h-6 rounded"
          />
          <span className="text-xs font-bold tracking-wider text-white">FIRMASTATE</span>
        </div>

        {/* Machine Info */}
        {machineName && (
          <>
            <div className="h-3 w-px bg-white/10" />
            <div className="flex items-center space-x-2 min-w-0">
              <span className="text-sm font-mono text-white truncate">{machineName}</span>
              {machineHash && (
                <span className="text-[10px] font-mono text-gray-600">
                  {machineHash.substring(0, 8)}
                </span>
              )}
              <Badge 
                variant="outline" 
                className={cn("text-[9px] px-1.5 py-0 h-4", statusColors[machineStatus])}
              >
                {machineStatus}
              </Badge>
            </div>
          </>
        )}
      </div>

      {/* Right: Operational Signals */}
      <div className="flex items-center space-x-3">
        {/* Sync Status (subtle) */}
        <div className={cn("text-[10px] font-mono", syncColors[syncStatus])}>
          {syncStatus}
        </div>
        
        <div className="h-3 w-px bg-white/10" />

        {/* Live Time & Uptime */}
        <div className="flex items-center space-x-3 text-[10px] font-mono text-gray-500">
          <span title="Local Time">{formatTime()}</span>
          <span className="text-gray-600">|</span>
          <span title="Session Uptime" className="text-gray-600">
            uptime {formatUptime()}
          </span>
        </div>

        <div className="h-3 w-px bg-white/10" />

        {/* Command Palette (Primary Action) */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onCommandPaletteOpen}
          className="h-7 px-2"
          data-testid="command-palette-trigger"
        >
          <Command className="w-3.5 h-3.5" />
          <span className="text-[10px] ml-1.5 text-gray-500">⌘K</span>
        </Button>

        {/* Refresh (Subtle) */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          className="h-7 w-7 p-0 text-gray-500 hover:text-gray-300"
          data-testid="refresh-button"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>
    </header>
  );
};

export default CommandBar;
